import createClient, { type Client, type Middleware } from 'openapi-fetch';
import type { paths } from './gen/schema';

/** Resolves the current session bearer token, or null when unauthenticated. */
export type TokenProvider = () => string | null;

/** Invoked when the backend returns 401 — the session is invalid/expired. */
export type UnauthorizedHandler = () => void;

export interface ApiClientOptions {
  baseUrl: string;
  getToken: TokenProvider;
  onUnauthorized?: UnauthorizedHandler;
}

export type ApiClient = Client<paths>;

/**
 * Build the typed REST client. Paths/params/request-bodies are derived from the
 * backend OpenAPI (`openapi.json`, regenerated via `bun run generate`). Auth is
 * attached as a Bearer header from {@link ApiClientOptions.getToken}; a 401 fires
 * {@link ApiClientOptions.onUnauthorized} so the app can redirect to /login.
 *
 * The client never holds provider keys and never sends tenant/hotel scope in a
 * body — scope lives in the path (see the api catalogue).
 */
export function createApiClient(options: ApiClientOptions): ApiClient {
  const client = createClient<paths>({ baseUrl: options.baseUrl });

  const authMiddleware: Middleware = {
    onRequest({ request }) {
      const token = options.getToken();
      if (token) {
        request.headers.set('Authorization', `Bearer ${token}`);
      }
      return request;
    },
    onResponse({ response }) {
      if (response.status === 401) {
        options.onUnauthorized?.();
      }
      return response;
    },
  };

  client.use(authMiddleware);
  return client;
}
